import { default as AntDatePicker } from "antd/lib/date-picker";

import { pipe } from "fp";
import moment from "moment";
import React from "react";

import { type, creator, reducer, effects, useDispatch } from "~/Action";
import * as ELD from "~/ELD";
import { Time } from "~/Time";

/**
 * Whether a day is outside the window the page can answer for: later than
 * today, which nothing has been submitted for, or earlier than the retention
 * period, which is no longer held. Compared by day rather than by instant, so
 * today itself and the first retained day both stay selectable.
 *
 * The control is bounded rather than the request left to go through, because a
 * window we hold no data for comes back empty — which reads as a fault rather
 * than as a limit.
 */
export const isUnavailable = (date: moment.Moment): boolean =>
  date.isBefore(toMoment(ELD.earliestStart()), "day") ||
  date.isAfter(moment(), "day");

/**
 * The window the page reads, as a range the user can move, bounded at both ends
 * by {@link isUnavailable}.
 */
export const Component: React.Fn = () =>
  pipe(useDispatch(), (dispatch) =>
    pipe(ELD.useInterval(), ({ interval }) => (
      <AntDatePicker.RangePicker
        allowClear={false}
        value={[toMoment(interval.start), toMoment(interval.stop)]}
        disabledDate={isUnavailable}
        onChange={(range) =>
          pipe([range?.[0], range?.[1]] as const, ([start, stop]) =>
            start && stop
              ? dispatch(
                  Action.Updated.create({
                    start: Time.DateTime.fromNumber(
                      start.startOf("day").valueOf()
                    ),
                    stop: Time.DateTime.fromNumber(stop.endOf("day").valueOf()),
                  })
                )
              : undefined
          )
        }
      />
    ))
  );

const toMoment = ({ dateTime }: Time.DateTime) => moment(dateTime);

export namespace Action {
  export namespace Updated {
    export const { TYPE, create, reduce } = pipe(
      type("ELDs updated interval" as const),
      creator(
        (interval: {
          readonly start: Time.DateTime;
          readonly stop: Time.DateTime;
        }) => ({
          interval,
        })
      ),
      reducer(
        ({ state, action: { interval } }) => (state.elds.interval = interval)
      ),
      // The window is a query argument, not a client-side filter — the rows for a
      // new range have not been fetched yet.
      effects(({ dispatch }) => dispatch(ELD.Action.Requested.create()))
    );
  }
}
